/**
 * Command Palette Store
 *
 * Zustand store for command palette state, including:
 * - Recent items list (persisted to localStorage)
 * - Methods for adding/clearing recent items
 */

import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";

export interface RecentItem {
  id: string;
  type: "navigation" | "action";
  label: string;
  url?: string;
  visitedAt: string;
}

/**
 * Maximum number of recent items to keep.
 */
const MAX_RECENT_ITEMS = 10;

/**
 * localStorage key for recent items persistence.
 */
export const COMMAND_PALETTE_STORAGE_KEY = "stream.commandPalette.recent";

export interface CommandPaletteState {
  /** List of recently visited items (max 10). */
  recentItems: RecentItem[];

  /**
   * Add a recent item to the list.
   * Deduplicates by id and moves existing items to the top.
   * Trims list to MAX_RECENT_ITEMS.
   */
  addRecentItem: (item: Omit<RecentItem, "visitedAt">) => void;

  /**
   * Remove a specific recent item by its ID.
   */
  removeRecentItem: (id: string) => void;

  /**
   * Clear all recent items.
   */
  clearRecentItems: () => void;
}

export const useCommandPaletteStore = create<CommandPaletteState>()(
  persist(
    (set) => ({
      recentItems: [],

      addRecentItem: (item) =>
        set((state) => {
          // Remove existing item with same id (dedupe)
          const filtered = state.recentItems.filter((r) => r.id !== item.id);

          // Create new item with current timestamp
          const newItem: RecentItem = {
            ...item,
            visitedAt: new Date().toISOString(),
          };

          // Add to front and trim to max
          const updated = [newItem, ...filtered].slice(0, MAX_RECENT_ITEMS);

          return { recentItems: updated };
        }),

      removeRecentItem: (id) =>
        set((state) => ({
          recentItems: state.recentItems.filter((r) => r.id !== id),
        })),

      clearRecentItems: () => set({ recentItems: [] }),
    }),
    {
      name: COMMAND_PALETTE_STORAGE_KEY,
      storage: createJSONStorage(() => {
        if (typeof window === "undefined") {
          return {
            getItem: () => null,
            setItem: () => {},
            removeItem: () => {},
          };
        }
        return window.localStorage;
      }),
      version: 1,
    },
  ),
);
